import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { COMPANY_SETTINGS } from "@client";
import { getCurrency, PriceFormats, TimeModifiers, EstimationUnits } from "@repo/utils";
import {
  getCompanySettingsQuery,
  getCompanyRateCardQuery,
  setCompanySettingsQuery,
  reorderCompanyRateCardCategories,
  reorderCompanyRateCardRoles,
} from "@query";
import {
  TableContainer,
  ValueCell,
  PinnableCell,
  HeaderRow,
  ExpandContainer,
} from "@tools";
import { CurrencyEditor } from "@dialogs";
import { InfoLabel, SortableList } from "@components";
import { Grid, LinearProgress } from "@material-ui/core";
import { EstimationUnitSetter, PriceFormatSetter } from "../../components";
import { RateCategory, NewRateElement, NewRateElementRow } from "./components";
import { makeStyles } from "@material-ui/styles";

export const Rates = () => {
  const { t } = useTranslation();
  const classes = useStyle();

  const [ currency, setCurrency ] = useState(null);
  const [ companyCurrency, setCompanyCurrency ] = useState(null);
  const [ dayTime, setDayTime ] = useState(null);
  const [ isLoading, setLoading ] = useState(true);
  const [ categories, setCategories ] = useState([]);

  const [ priceFormat, setPriceFormat ] = useState(PriceFormats.EUROPEAN);
  const [ estimationUnit, setEstimationUnit ] = useState(EstimationUnits.HOURS);
  const [ headerRef, setHeaderRef ] = useState(null);
  const [ addingNewWorkType, setAddingNewWorkType ] = useState(false);
  
  const usedElementNames = useMemo(() => (
    categories.map(w => w.name).filter(w => w)
  ), [ categories ]);

  const loadData = useCallback(async () => {
    setLoading(true);

    const [ currencyCode, companyCurrencyCode, timeEquiv, rateCard ] = await Promise.all([
      getCompanySettingsQuery(COMPANY_SETTINGS.CURRENCY_DEF),
      getCompanySettingsQuery(COMPANY_SETTINGS.COMPANY_CURRENCY),
      getCompanySettingsQuery(COMPANY_SETTINGS.TIME_EQUIV),
      getCompanyRateCardQuery(),
    ]);
    
    setDayTime(timeEquiv?.day || TimeModifiers.DAY);
    setCurrency(getCurrency(currencyCode || "USD"));
    setCompanyCurrency(getCurrency(companyCurrencyCode || "USD"));
    setCategories(rateCard);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const handleCurrencyChange = useCallback((newCurrency) => {
    setCurrency(newCurrency);
    setCompanySettingsQuery(COMPANY_SETTINGS.CURRENCY_DEF, newCurrency.code);
  }, []);

  const handleCompanyCurrencyChange = useCallback((newCurrency) => {
    setCompanyCurrency(newCurrency);
    setCompanySettingsQuery(COMPANY_SETTINGS.COMPANY_CURRENCY, newCurrency.code);
  }, []);

  const handleChange = useCallback((param, parentId, id, value) => {
    setCategories((current) =>
      current.map((workType) => {
        if (!parentId && workType.categoryId === id)
          return { ...workType, [param]: value };

        if (parentId && workType.categoryId === parentId) {
          return {
            ...workType,
            roles: workType.roles.map((member) =>
              member.id === id ? { ...member, [param]: value } : member
            ),
          };
        }

        return workType;
      })
    );
  }, []);

  const handleRemove = useCallback((parentId, id) => {
    setCategories((current) => {
      if (!parentId)
        return current.filter((workType) => workType.categoryId !== id);

      return current.map((workType) => {
        if(workType.categoryId === parentId) {
          return {
            ...workType,
            roles: workType.roles.filter((member) => member.id !== id),
          };
        }
        return workType;
      });
    });
  }, []);
  
  const handleAdd = useCallback((parentId, newElement) => {
    setCategories((current) => {
      if (!parentId)
        return [ ...current, newElement ];
      
      return current.map((workType) => {
        if(workType.categoryId === parentId) {
          return {
            ...workType,
            roles: [ ...workType.roles, newElement ],
          };
        }
        return workType;
      });
    });
  }, []);
  
  const handleCategoryReorder = async (list, categoryId, _, oldIndex, newIndex) => {
    setCategories((current) => (
      list.map(e => current.find(c => c.categoryId === e.id))
    ));
    if(oldIndex !== newIndex)
      await reorderCompanyRateCardCategories(categoryId, newIndex, oldIndex);
  };
  
  const handleRoleReorder = async (categoryId, list, roleId, oldIndex, newIndex) => {
    setCategories((current) => (
      current.map((workType) => {
        if(workType.categoryId === categoryId) {
          return {
            ...workType,
            roles: list.map(e => workType.roles.find(c => c.id === e.id)),
          };
        }
        return workType;
      })
    ));
    if(oldIndex !== newIndex)
      await reorderCompanyRateCardRoles(categoryId, roleId, newIndex, oldIndex)
  };
  
  return (
    <Grid item container direction="column">
      <PriceFormatSetter onChange={setPriceFormat} />
      <EstimationUnitSetter onChange={setEstimationUnit} />

      <InfoLabel
        label={t("views.settings.projects.rate_card.title", {
          timely: t(`timely.${ estimationUnit === EstimationUnits.HOURS ? "hour" : "day" }`)
        })}
        translate={false}
      />
      
      { isLoading && <LinearProgress variant="indeterminate" /> }

      {
        !isLoading &&
        <TableContainer
          tableHeaderRef={headerRef}
          tableContainerIdentifier="ratesTable"
          className={classes.tableContainer}
        >
          <HeaderRow
            ref={setHeaderRef}
          >
            <PinnableCell
              keepSmallestSize
              pinnedLeft
              allowOverflowDisplay
              isHeader
              containers={<ExpandContainer isHeader />}
            >
              <span>Name</span>
            </PinnableCell>
            <ValueCell isHeader size="small">
              <span>Color</span>
            </ValueCell>
            <ValueCell isHeader size="large" extendPadding>
              <span className="w-128">
                {t("views.settings.projects.rate_card.company_currency", {
                  timely: t(`timely.${ estimationUnit === EstimationUnits.HOURS ? "hour" : "day" }`),
                  currency: companyCurrency.code,
                })}
              </span>
              <Grid
                item container
                direction="column"
                justifyContent="center"
                alignItems="center"
                className="absolute w-max"
                style={{ right: -16 }}
              >
                <CurrencyEditor
                  currency={companyCurrency.code}
                  title={t("views.settings.projects.rate_card.company_currency_select", {
                    timely: t(`timely.${ estimationUnit === EstimationUnits.HOURS ? "hour" : "day" }`)
                  })}
                  onChange={handleCompanyCurrencyChange}
                />
              </Grid>
            </ValueCell>
            <ValueCell isHeader size="large" extendPadding>
              <span className="w-128">
                {t("views.settings.projects.rate_card.currency", {
                  timely: t(`timely.${ estimationUnit === EstimationUnits.HOURS ? "hour" : "day" }`),
                  currency: currency.code,
                })}
              </span>
              <Grid
                item container
                direction="column"
                justifyContent="center"
                alignItems="center"
                className="absolute w-max"
                style={{ right: -16 }}
              >
                <CurrencyEditor
                  currency={currency.code}
                  title={t("views.settings.projects.rate_card.currency_select", {
                    timely: t(`timely.${ estimationUnit === EstimationUnits.HOURS ? "hour" : "day" }`)
                  })}
                  onChange={handleCurrencyChange}
                />
              </Grid>
            </ValueCell>
            <ValueCell isHeader size="small" />
          </HeaderRow>
          
          <SortableList
            list={categories}
            path="category"
            group="category"
            itemIdKey="categoryId"
            onListReorder={handleCategoryReorder}
            displayAsColumnFlexbox
          >
            {categories.map((workType) => (
              <RateCategory
                key={`workType-${workType.categoryId}`}
                workType={workType}
                currency={currency}
                companyCurrency={companyCurrency}
                dayTime={dayTime}
                americanPriceFormat={priceFormat === PriceFormats.AMERICAN}
                estimationUnit={estimationUnit}
                onChange={handleChange}
                onRemove={handleRemove}
                onTeamMemberAdd={handleAdd}
                onRoleReorder={handleRoleReorder}
                usedElementNames={usedElementNames}
              />
            ))}
          </SortableList>
          
          {
            addingNewWorkType
              ? <NewRateElement
                level={1}
                usedElementNames={usedElementNames}
                onCreate={handleAdd}
                onCancel={() => setAddingNewWorkType(false)}
              />
              : <NewRateElementRow
                isWorkType
                onClick={() => setAddingNewWorkType(true)}
              />
          }
          {!categories.length && (
            <Grid item container role="row" wrap="nowrap">
              <ValueCell size="large">
                <span className="transparent-2">
                  {t("views.settings.projects.rate_card.not_found")}
                </span>
              </ValueCell>
            </Grid>
          )}
        </TableContainer>
      }
    </Grid>
  );
};

const useStyle = makeStyles(() => ({
  tableContainer: {
    marginLeft: -32,
    width: "calc(100% + 32px)"
  }
}));